import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { playTone, playGong, playBowl, getCtx } from "./audio";
import { Volume2 } from "lucide-react";

/* ---------- 1. L'Oracle (Gong) ---------- */
export function Oracle() {
  const [strikes, setStrikes] = useState(0);
  return (
    <div className="text-center">
      <p className="text-ink/70 mb-8 font-light">Frappez le gong et laissez résonner.</p>
      <motion.button
        whileTap={{ scale: 0.92 }}
        onClick={() => { playGong(); setStrikes((s) => s + 1); }}
        className="relative w-56 h-56 md:w-64 md:h-64 rounded-full mx-auto"
        style={{ background: "radial-gradient(circle at 35% 30%, #D4A574, #8B5E2A 60%, #4A3320)", boxShadow: "0 30px 60px rgba(74,51,32,0.4)" }}
        data-testid="oracle-gong"
      >
        {[...Array(strikes)].slice(-3).map((_, i) => (
          <motion.span
            key={`${strikes}-${i}`}
            className="absolute inset-0 rounded-full border-2 border-terracotta"
            initial={{ scale: 1, opacity: 0.7 }}
            animate={{ scale: 2.2, opacity: 0 }}
            transition={{ duration: 2.5, ease: "easeOut" }}
          />
        ))}
        <span className="font-serif text-5xl text-cream/80">緣</span>
      </motion.button>
      <p className="mt-6 text-xs uppercase tracking-[0.2em] text-ink/40">Frappes : {strikes}</p>
    </div>
  );
}

/* ---------- 2. Oreille Absolue ---------- */
const EAR_NOTES = [261.63, 329.63, 392, 523.25]; // C4, E4, G4, C5
export function EarMatch() {
  const [target, setTarget] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const start = () => { setTarget(Math.floor(Math.random() * 4)); setFeedback(null); };
  useEffect(() => { start(); }, []);
  const playTarget = () => target !== null && playTone(EAR_NOTES[target], 1.2, "sine");
  const guess = (i) => {
    playTone(EAR_NOTES[i], 1, "sine");
    setFeedback(i === target ? "✓ Bravo, oreille fine !" : "✗ Pas tout à fait, réessayez.");
  };
  return (
    <div className="text-center">
      <p className="text-ink/70 mb-6 font-light">Écoutez la cible, puis trouvez sa jumelle parmi les 4.</p>
      <button onClick={playTarget} className="px-8 py-4 rounded-full bg-terracotta text-cream text-sm uppercase tracking-[0.15em] hover:scale-105 transition mb-8" data-testid="ear-target">
        🎯 Écouter la cible
      </button>
      <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
        {EAR_NOTES.map((_, i) => (
          <button key={i} onClick={() => guess(i)} className="aspect-square rounded-2xl bg-sand hover:bg-cream border border-ink/10 hover:border-terracotta transition font-serif text-3xl text-ink" data-testid={`ear-option-${i}`}>
            {["Do", "Mi", "Sol", "Do↑"][i]}
          </button>
        ))}
      </div>
      {feedback && <p className="mt-6 font-serif text-xl text-terracotta">{feedback}</p>}
      <button onClick={start} className="mt-4 text-xs uppercase tracking-[0.2em] text-ink/50 hover:text-terracotta">↺ Nouvelle cible</button>
    </div>
  );
}

/* ---------- 3. Mémoire Sonore (Simon) ---------- */
const MEM_NOTES = [261.63, 329.63, 392, 523.25];
const MEM_COLORS = ["#C86B52", "#8C9A86", "#A8843F", "#4A3320"];
export function Memory() {
  const [seq, setSeq] = useState([]);
  const [user, setUser] = useState([]);
  const [playing, setPlaying] = useState(false);
  const [active, setActive] = useState(-1);
  const [msg, setMsg] = useState("Cliquez pour démarrer");
  const next = () => {
    const s = [...seq, Math.floor(Math.random() * 4)];
    setSeq(s); setUser([]); setMsg(`Niveau ${s.length}`);
    setPlaying(true);
    s.forEach((n, i) => setTimeout(() => { setActive(n); playTone(MEM_NOTES[n], 0.4); setTimeout(() => setActive(-1), 350); }, i * 600 + 400));
    setTimeout(() => setPlaying(false), s.length * 600 + 400);
  };
  const tap = (i) => {
    if (playing) return;
    playTone(MEM_NOTES[i], 0.3); setActive(i); setTimeout(() => setActive(-1), 200);
    const u = [...user, i]; setUser(u);
    if (seq[u.length - 1] !== i) { setMsg(`Game over · Niveau ${seq.length}`); setSeq([]); return; }
    if (u.length === seq.length) setTimeout(next, 700);
  };
  return (
    <div className="text-center">
      <p className="text-ink/70 mb-6 font-light">Reproduisez la séquence sonore.</p>
      <div className="grid grid-cols-2 gap-4 max-w-sm mx-auto mb-6">
        {MEM_NOTES.map((_, i) => (
          <motion.button key={i} onClick={() => tap(i)} animate={{ scale: active === i ? 1.1 : 1, opacity: active === i ? 1 : 0.85 }} className="aspect-square rounded-2xl" style={{ background: MEM_COLORS[i] }} data-testid={`memory-pad-${i}`} />
        ))}
      </div>
      <p className="font-serif text-xl text-ink mb-3">{msg}</p>
      <button onClick={next} disabled={playing} className="px-6 py-3 rounded-full bg-ink text-cream text-xs uppercase tracking-[0.15em] disabled:opacity-40">{seq.length === 0 ? "Démarrer" : "Suivant"}</button>
    </div>
  );
}

/* ---------- 4. Bol Chantant ---------- */
export function Bowl() {
  return (
    <div className="text-center">
      <p className="text-ink/70 mb-8 font-light">Cliquez pour faire chanter le bol.</p>
      <motion.button whileTap={{ scale: 0.95 }} onClick={() => playBowl(3.5)} className="w-56 h-56 md:w-64 md:h-64 rounded-full mx-auto flex items-center justify-center" style={{ background: "radial-gradient(circle at 30% 25%, #E8C99B, #B8884A 50%, #6B4A1F)", boxShadow: "0 30px 60px rgba(74,51,32,0.35)" }} data-testid="bowl-button">
        <span className="font-serif italic text-6xl text-cream/70">བོ</span>
      </motion.button>
      <p className="mt-6 text-xs uppercase tracking-[0.2em] text-ink/40">320 Hz · Tibet</p>
    </div>
  );
}

/* ---------- 5. Mantra OM (hold) ---------- */
export function Mantra() {
  const oscRef = useRef(null);
  const [holding, setHolding] = useState(false);
  const [scale, setScale] = useState(1);
  const start = () => {
    const ctx = getCtx();
    const osc = ctx.createOscillator(); const g = ctx.createGain();
    osc.type = "sine"; osc.frequency.value = 136.1; // OM frequency
    g.gain.setValueAtTime(0, ctx.currentTime);
    g.gain.linearRampToValueAtTime(0.25, ctx.currentTime + 0.5);
    osc.connect(g); g.connect(ctx.destination); osc.start();
    oscRef.current = { osc, g, ctx };
    setHolding(true);
    let s = 1;
    oscRef.current.interval = setInterval(() => { s = Math.min(s + 0.05, 1.6); setScale(s); }, 100);
  };
  const stop = () => {
    if (!oscRef.current) return;
    const { osc, g, ctx, interval } = oscRef.current;
    clearInterval(interval);
    g.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.8);
    osc.stop(ctx.currentTime + 0.9);
    oscRef.current = null; setHolding(false); setScale(1);
  };
  return (
    <div className="text-center">
      <p className="text-ink/70 mb-8 font-light">Maintenez pour faire monter le OM (136.1 Hz, fréquence sacrée).</p>
      <motion.button onMouseDown={start} onMouseUp={stop} onMouseLeave={holding ? stop : undefined} onTouchStart={start} onTouchEnd={stop} animate={{ scale }} transition={{ duration: 0.1 }} className="w-56 h-56 md:w-64 md:h-64 rounded-full mx-auto flex items-center justify-center select-none" style={{ background: "radial-gradient(circle, #C86B52, #4A3320)", boxShadow: holding ? "0 0 80px #C86B52" : "0 20px 40px rgba(74,51,32,0.3)" }} data-testid="mantra-button">
        <span className="font-serif text-7xl text-cream">ॐ</span>
      </motion.button>
    </div>
  );
}

/* ---------- 6. 432 vs 440 ---------- */
export function Frequency() {
  const [target, setTarget] = useState(null);
  const [msg, setMsg] = useState(null);
  const start = () => { setTarget(Math.random() < 0.5 ? 432 : 440); setMsg(null); };
  useEffect(() => { start(); }, []);
  const playRef = () => target && playTone(target, 1.5, "sine", 0.3);
  const guess = (f) => { playTone(f, 0.6); setMsg(f === target ? "✓ Oreille divine !" : `✗ C'était ${target} Hz`); };
  return (
    <div className="text-center">
      <p className="text-ink/70 mb-6 font-light">Écoutez la fréquence — sacrée (432 Hz) ou standard (440 Hz) ?</p>
      <button onClick={playRef} className="px-8 py-4 rounded-full bg-terracotta text-cream text-sm uppercase tracking-[0.15em] hover:scale-105 transition mb-8 inline-flex items-center gap-2" data-testid="freq-listen">
        <Volume2 size={16}/> Écouter
      </button>
      <div className="flex gap-4 justify-center">
        <button onClick={() => guess(432)} className="px-8 py-6 rounded-2xl bg-sand hover:bg-cream border border-ink/10 hover:border-terracotta transition" data-testid="freq-432">
          <div className="font-serif text-3xl text-ink">432 Hz</div><div className="text-xs text-ink/55 mt-1">Sacrée</div>
        </button>
        <button onClick={() => guess(440)} className="px-8 py-6 rounded-2xl bg-sand hover:bg-cream border border-ink/10 hover:border-terracotta transition" data-testid="freq-440">
          <div className="font-serif text-3xl text-ink">440 Hz</div><div className="text-xs text-ink/55 mt-1">Standard</div>
        </button>
      </div>
      {msg && <p className="mt-6 font-serif text-xl text-terracotta">{msg}</p>}
      <button onClick={start} className="mt-4 text-xs uppercase tracking-[0.2em] text-ink/50 hover:text-terracotta block mx-auto">↺ Nouvelle écoute</button>
    </div>
  );
}

export const GAMES = [
  { id: "oracle", emoji: "🔔", name: "L'Oracle", desc: "Frappez le gong sacré.", Component: Oracle },
  { id: "ear", emoji: "👂", name: "Oreille Absolue", desc: "Retrouvez la note entendue.", Component: EarMatch },
  { id: "memory", emoji: "🧠", name: "Mémoire Sonore", desc: "Reproduisez la séquence.", Component: Memory },
  { id: "bowl", emoji: "🪔", name: "Bol Chantant", desc: "Faites résonner le bol.", Component: Bowl },
  { id: "mantra", emoji: "🕉️", name: "Mantra OM", desc: "Tenez pour chanter.", Component: Mantra },
  { id: "frequency", emoji: "🎵", name: "432 vs 440", desc: "Devinez la fréquence sacrée.", Component: Frequency },
];
